import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@/types";
import { bcPhoneMessages, genericMessages } from "@/constants/errorMessages";
import { useOnboarding } from "@/features/onboarding";
import { useTerms } from "@/features/terms/hooks/useTerms";
import {
  BcAttemptState,
  getPhoneVerificationErrorMessage,
  PhoneVerificationError,
  ScoringErrorCode,
  useScoring,
  type BcPhoneNumber,
} from "@/features/scoring";
import { routes } from "@/routes/routes";
import { BotMessage } from "../../components/BotMessage";
import { TurnMessages } from "../../components/TurnMessages";
import { QuickReplies, type QuickReply } from "../../components/QuickReplies";
import { ChatComposer } from "../../components/ChatComposer";
import { ChatTurnLayout } from "../../components/ChatTurnLayout";
import { useTurnMessages } from "../../hooks/useTurnMessages";
import { formatSeconds, useCountdown } from "../../hooks/useCountdown";
import { chatCopy } from "../../engine/copy";
import type { ChatTurnProps } from "../../types/chat";
import { bcConsentLines } from "../consentLines";

const CODE_LENGTH = 4;
const RESEND_DELAY = 120;

const Reply = {
  AcceptConsent: "accept-consent",
  WhyConsent: "why-consent",
  OtherMethod: "other-method",
  Retry: "retry",
  Resend: "resend",
  ChangePhone: "change-phone",
} as const;

type Phase =
  | { kind: "checking" }
  | { kind: "consent" }
  | { kind: "kyc" }
  | { kind: "phones" }
  | { kind: "code"; phone: BcPhoneNumber }
  | { kind: "failed" };

// The BC score in the chat: starts it, asks for the consent if it is
// missing, then confirms one of the phone numbers the bureau knows by SMS.
// Once scored, the refreshed scoring state moves the chat on.
export function BcScoringTurn({ onAnswered }: ChatTurnProps) {
  const navigate = useNavigate();
  const { messages, addUser, addBot, addError } = useTurnMessages();
  const [phase, setPhase] = useState<Phase>({ kind: "checking" });
  const {
    bcPhones,
    isBcPhonesLoading,
    initiateScoreObtaining,
    sendVerificationCode,
    isSendingCode,
    generateByCode,
    isGeneratingByCode,
    refreshScoringState,
  } = useScoring({ fetchBcPhones: phase.kind === "phones" });
  const { acceptBcTerms, isLoading: isAcceptingConsent } = useTerms();
  const { getOnboardingState } = useOnboarding();

  const [isConsentWhyAnswered, setIsConsentWhyAnswered] = useState(false);
  const [code, setCode] = useState("");
  const [codesSent, setCodesSent] = useState(0);
  const secondsLeft = useCountdown(RESEND_DELAY, codesSent);

  const start = useCallback(async () => {
    setPhase({ kind: "checking" });

    try {
      const attempt = await initiateScoreObtaining();

      if (attempt.termsRequired) {
        addBot(bcConsentLines);
        setPhase({ kind: "consent" });
        return;
      }

      // Backend running without phone scoring: a score needs KYC first.
      if (attempt.kycRequired) {
        addBot(chatCopy.bcKycNeeded);
        setPhase({ kind: "kyc" });
        return;
      }

      if (attempt.bcAttemptState === BcAttemptState.NeedPhoneVerification) {
        setPhase({ kind: "phones" });
        return;
      }

      if (attempt.bcAttemptState === BcAttemptState.Undefined) {
        addError(chatCopy.bcFailed[0]);
        setPhase({ kind: "failed" });
        return;
      }

      onAnswered();
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.message === ScoringErrorCode.TermsNotAccepted
      ) {
        addBot(bcConsentLines);
        setPhase({ kind: "consent" });
        return;
      }

      addError(chatCopy.bcFailed[0]);
      setPhase({ kind: "failed" });
    }
  }, [initiateScoreObtaining, addBot, addError, onAnswered]);

  const hasStarted = useRef(false);
  useEffect(() => {
    if (hasStarted.current) {
      return;
    }
    hasStarted.current = true;
    void start();
  }, [start]);

  // The phone list arrives after the phase switch.
  const hasListedPhones = useRef(false);
  useEffect(() => {
    if (phase.kind !== "phones" || isBcPhonesLoading || !bcPhones) {
      return;
    }
    if (hasListedPhones.current) {
      return;
    }
    hasListedPhones.current = true;
    addBot(bcPhones.length > 0 ? chatCopy.bcPhones : chatCopy.bcNoPhones);
  }, [phase.kind, isBcPhonesLoading, bcPhones, addBot]);

  const sendCodeTo = async (phone: BcPhoneNumber) => {
    try {
      const attempt = await sendVerificationCode(phone.id);
      // A failed SMS still answers HTTP 200.
      if (attempt.phoneVerificationError !== PhoneVerificationError.None) {
        addError(bcPhoneMessages.sms_failed);
        return false;
      }
      setCodesSent((count) => count + 1);
      return true;
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.message === ScoringErrorCode.AlreadyHasScore
      ) {
        await refreshScoringState();
        onAnswered();
        return false;
      }
      addError(bcPhoneMessages.unexpected);
      return false;
    }
  };

  const handlePhone = async (phone: BcPhoneNumber) => {
    addUser(phone.phone);
    if (await sendCodeTo(phone)) {
      addBot(chatCopy.bcCode);
      setPhase({ kind: "code", phone });
    }
  };

  const handleCode = async (value: string) => {
    addUser(value);
    setCode("");

    try {
      const attempt = await generateByCode(value);
      if (attempt.bcAttemptState === BcAttemptState.Completed) {
        onAnswered();
        return;
      }
      addError(
        getPhoneVerificationErrorMessage(attempt.phoneVerificationError),
      );
    } catch {
      addError(bcPhoneMessages.unexpected);
    }
  };

  const handleCodeChange = (value: string) =>
    setCode(value.replace(/\D/g, "").slice(0, CODE_LENGTH));

  const handleReply = async (reply: string) => {
    switch (reply) {
      case Reply.AcceptConsent:
        addUser("Da, sunt de acord");
        try {
          await acceptBcTerms();
          await getOnboardingState();
        } catch {
          addError(genericMessages.unexpected);
          return;
        }
        addBot(chatCopy.bcChecking);
        void start();
        return;
      case Reply.WhyConsent:
        addUser("De ce este necesar?");
        addBot(chatCopy.consentWhy);
        setIsConsentWhyAnswered(true);
        return;
      case Reply.OtherMethod:
        navigate(routes.kyc.base);
        return;
      case Reply.Retry:
        addUser("Încearcă din nou");
        addBot(chatCopy.bcChecking);
        void start();
        return;
      case Reply.ChangePhone:
        addUser("Schimbă numărul");
        addBot([chatCopy.bcPhones[1]]);
        setPhase({ kind: "phones" });
        return;
      case Reply.Resend:
        if (phase.kind === "code") {
          addUser("Retrimite codul");
          if (await sendCodeTo(phase.phone)) {
            addBot(chatCopy.bcCodeResent);
          }
        }
        return;
      default: {
        const phone = bcPhones?.find((item) => item.id === reply);
        if (phone) {
          void handlePhone(phone);
        }
      }
    }
  };

  const isBusy = isSendingCode || isGeneratingByCode || isAcceptingConsent;

  const replies = ((): QuickReply[] => {
    switch (phase.kind) {
      case "consent":
        return [
          { id: Reply.AcceptConsent, label: "Da, sunt de acord 👍" },
          ...(isConsentWhyAnswered
            ? []
            : [{ id: Reply.WhyConsent, label: "De ce este necesar? 🤔" }]),
        ];
      case "kyc":
        return [{ id: Reply.OtherMethod, label: "Începe verificarea →" }];
      case "phones":
        if (isBcPhonesLoading || !bcPhones) {
          return [];
        }
        return [
          ...bcPhones.map((phone) => ({ id: phone.id, label: phone.phone })),
          {
            id: Reply.OtherMethod,
            label:
              bcPhones.length > 0
                ? "Nu apare numărul meu"
                : "Continuă cu verificarea identității →",
          },
        ];
      case "code":
        return [
          {
            id: Reply.Resend,
            label:
              secondsLeft === 0
                ? "Retrimite codul"
                : `Retrimite codul (${formatSeconds(secondsLeft)})`,
            disabled: secondsLeft > 0,
          },
          { id: Reply.ChangePhone, label: "Schimbă numărul" },
        ];
      case "failed":
        return [{ id: Reply.Retry, label: "Încearcă din nou" }];
      default:
        return [];
    }
  })();

  return (
    <ChatTurnLayout
      messageCount={messages.length}
      thread={
        <>
          {/* A live status, typed out even when the step was open already. */}
          <BotMessage lines={chatCopy.bcChecking} isTyped />
          <TurnMessages messages={messages} />
        </>
      }
      actions={
        <>
          {replies.length > 0 && (
            <QuickReplies
              disabled={isBusy}
              onSelect={(reply) => void handleReply(reply)}
              replies={replies}
            />
          )}
          {phase.kind === "code" && (
            <ChatComposer
              label="Codul din SMS"
              placeholder="Scrie codul din SMS"
              inputMode="numeric"
              autoComplete="one-time-code"
              enterKeyHint="send"
              maxLength={CODE_LENGTH}
              value={code}
              onChange={handleCodeChange}
              onSubmit={() => void handleCode(code)}
              isLoading={isGeneratingByCode}
            />
          )}
        </>
      }
    />
  );
}
